import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Home,
  User,
  FileText,
  Upload,
  Calendar,
  History,
  CheckSquare,
  Settings,
  Users,
  Building,
  Shield,
  BarChart,
  Activity,
} from 'lucide-react';
import { cn } from '@utils/cn';
import { Badge } from '@components/common/Badge';
import type { RootState } from '@store/index';

interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
  badge?: string | number;
}

const menuItems: MenuItem[] = [
  // Applicant Menu
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: <Home className="h-5 w-5" />,
    roles: ['APPLICANT'],
  },
  {
    id: 'profile',
    label: 'My Profile',
    path: '/profile',
    icon: <User className="h-5 w-5" />,
    roles: ['APPLICANT'],
  },
  {
    id: 'apply',
    label: 'Apply for License',
    path: '/apply',
    icon: <FileText className="h-5 w-5" />,
    roles: ['APPLICANT'],
  },
  {
    id: 'documents',
    label: 'Documents',
    path: '/documents',
    icon: <Upload className="h-5 w-5" />,
    roles: ['APPLICANT'],
    badge: '2',
  },
  {
    id: 'booking',
    label: 'Book Appointment',
    path: '/booking',
    icon: <Calendar className="h-5 w-5" />,
    roles: ['APPLICANT'],
  },
  {
    id: 'appointments',
    label: 'My Appointments',
    path: '/appointments',
    icon: <History className="h-5 w-5" />,
    roles: ['APPLICANT'],
  },
  {
    id: 'status',
    label: 'Application Status',
    path: '/status',
    icon: <CheckSquare className="h-5 w-5" />,
    roles: ['APPLICANT'],
  },

  // GM Licensing Menu
  {
    id: 'admin-dashboard',
    label: 'Dashboard',
    path: '/admin',
    icon: <BarChart className="h-5 w-5" />,
    roles: ['ADMIN', 'OPS'],
  },
  {
    id: 'applicants',
    label: 'Applicants',
    path: '/admin/applicants',
    icon: <Users className="h-5 w-5" />,
    roles: ['ADMIN', 'OPS'],
  },
  {
    id: 'enrolments',
    label: 'Enrolments',
    path: '/admin/enrolments',
    icon: <FileText className="h-5 w-5" />,
    roles: ['ADMIN', 'OPS'],
  },
  {
    id: 'centers',
    label: 'Testing Centers',
    path: '/admin/centers',
    icon: <Building className="h-5 w-5" />,
    roles: ['ADMIN'],
  },
  {
    id: 'bookings',
    label: 'Bookings',
    path: '/admin/bookings',
    icon: <Calendar className="h-5 w-5" />,
    roles: ['ADMIN', 'OPS'],
  },
  {
    id: 'users',
    label: 'User Management',
    path: '/admin/users',
    icon: <Shield className="h-5 w-5" />,
    roles: ['ADMIN'],
  },
  {
    id: 'audit',
    label: 'Audit Logs',
    path: '/admin/audit',
    icon: <Activity className="h-5 w-5" />,
    roles: ['ADMIN'],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    path: '/admin/analytics',
    icon: <BarChart className="h-5 w-5" />,
    roles: ['ADMIN'],
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/admin/settings',
    icon: <Settings className="h-5 w-5" />,
    roles: ['ADMIN'],
  },
];

interface SidebarProps {
  isOpen: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  // Filter menu items by user role
  const filteredMenu = menuItems.filter((item) =>
    item.roles.includes(user?.role || '')
  );

  return (
    <aside className={cn(
      "fixed inset-y-0 left-0 w-64 bg-white shadow-lg border-r border-gray-200 z-40 mt-16 transition-transform duration-300",
      // Always visible on desktop (lg screens and up)
      "lg:translate-x-0",
      // On mobile, slide in/out based on isOpen state
      isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
    )}>
      <nav className="h-full overflow-y-auto py-6 px-3">
        <div className="space-y-1">
          {filteredMenu.map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  'flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200',
                  isActive
                    ? 'bg-ncaa-primary text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <div className="flex items-center space-x-3">
                  <span className={cn(isActive && 'scale-110')}>
                    {item.icon}
                  </span>
                  <span className="font-medium">{item.label}</span>
                </div>

                {item.badge && (
                  <Badge
                    variant={isActive ? 'default' : 'warning'}
                    size="sm"
                    className={cn(
                      isActive && 'bg-white bg-opacity-20 text-white border-white border-opacity-30'
                    )}
                  >
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </aside>
  );
};
